import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Trash2, RefreshCw, Pencil, Check } from 'lucide-react';
import { message } from 'antd';
import { V, labelStyle, h2Style } from '../vividGlass';
import brandAssetActions from '../../../../actions/brandAssets';
import { ASSET_TYPES_CONFIG, CAPTION_STATUS } from './assetConstants';

const PURPOSES = [
  { value: 'brand_identity', label: 'Brand Asset', desc: 'Used by AI for content generation' },
  { value: 'inspiration', label: 'Reference', desc: 'Style & mood inspiration' },
];

const inputStyle = {
  flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${V.glassBorder}`,
  borderRadius: 8, padding: '4px 10px', color: V.text, fontSize: 14,
  outline: 'none', fontFamily: V.font,
};

export default function AssetDetailPanel({ asset: initialAsset, brandId, onClose, onDeleted, onUpdated }) {
  const [asset, setAsset] = useState(initialAsset);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(initialAsset.name || '');

  const patch = async (updates) => {
    try {
      await brandAssetActions.updateAsset(brandId, asset.asset_id, updates);
      const updated = { ...asset, ...updates };
      setAsset(updated);
      onUpdated?.(updated);
    } catch {
      message.error('Failed to update');
    }
  };

  const handleDelete = async () => {
    try {
      await brandAssetActions.deleteAsset(brandId, asset.asset_id);
      onDeleted?.(asset.asset_id);
      onClose();
    } catch {
      message.error('Failed to delete');
    }
  };

  const handleRecaption = async () => {
    try {
      await brandAssetActions.recaptionAsset(brandId, asset.asset_id);
      message.success('Caption regeneration queued');
    } catch {
      message.error('Failed to queue caption');
    }
  };

  const saveName = async () => {
    if (nameVal.trim()) await patch({ name: nameVal.trim() });
    setEditingName(false);
  };

  const captionStatus = CAPTION_STATUS[asset.caption_status] || CAPTION_STATUS.pending;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }} />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 32, stiffness: 340 }}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
          background: 'rgba(8,3,18,0.98)', backdropFilter: 'blur(24px)',
          borderLeft: `1px solid ${V.glassBorder}`,
          zIndex: 1200, display: 'flex', flexDirection: 'column', overflowY: 'auto',
          fontFamily: V.font,
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid rgba(255,255,255,0.07)`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: V.textMuted, display: 'flex', padding: 4 }}>
            <X size={18} />
          </button>

          <div style={{ flex: 1, overflow: 'hidden' }}>
            {editingName ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  autoFocus value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  style={inputStyle}
                />
                <button type="button" onClick={saveName}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4ade80', fontFamily: V.font, fontSize: 12, fontWeight: 600, padding: '4px 0' }}>
                  Save
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...h2Style, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {asset.name || asset.asset_type}
                </span>
                <button type="button" onClick={() => setEditingName(true)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: V.textMuted, display: 'flex', flexShrink: 0, padding: 0 }}>
                  <Pencil size={12} />
                </button>
              </div>
            )}
          </div>

          <button type="button" onClick={handleDelete}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'rgba(245,87,108,0.1)', border: '1px solid rgba(245,87,108,0.3)', borderRadius: 8, color: '#f5576c', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: V.font, flexShrink: 0 }}>
            <Trash2 size={12} /> Delete
          </button>
        </div>

        {/* Image preview */}
        <div style={{ background: 'rgba(0,0,0,0.4)', padding: 20, flexShrink: 0 }}>
          <img
            src={asset.url} alt={asset.name || ''}
            style={{ width: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 14, display: 'block' }}
          />
        </div>

        {/* Type */}
        <Section label="Type">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ASSET_TYPES_CONFIG.filter((t) => t.value !== 'auto').map((t) => (
              <button type="button" key={t.value} onClick={() => patch({ asset_type: t.value })}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', borderRadius: 99, fontFamily: V.font,
                  background: asset.asset_type === t.value ? `${t.color}22` : 'transparent',
                  border: `1px solid ${asset.asset_type === t.value ? t.color + '60' : V.glassBorder}`,
                  color: asset.asset_type === t.value ? t.color : V.textMuted,
                  fontSize: 12, cursor: 'pointer', transition: 'all 0.15s',
                }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.color, opacity: asset.asset_type === t.value ? 1 : 0.4 }} />
                {t.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Purpose */}
        <Section label="Purpose">
          <div style={{ display: 'flex', gap: 8 }}>
            {PURPOSES.map((p) => (
              <button type="button" key={p.value} onClick={() => patch({ usage_type: p.value })}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
                  fontFamily: V.font, textAlign: 'left', transition: 'all 0.15s',
                  background: asset.usage_type === p.value ? 'rgba(255,255,255,0.08)' : 'transparent',
                  border: `1px solid ${asset.usage_type === p.value ? V.glassBorderHover : V.glassBorder}`,
                }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: asset.usage_type === p.value ? V.text : V.textSub, marginBottom: 2 }}>{p.label}</div>
                <div style={{ fontSize: 11, color: V.textMuted }}>{p.desc}</div>
              </button>
            ))}
          </div>
        </Section>

        {/* AI Caption */}
        <Section label="AI Caption" style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{
              padding: '3px 10px', borderRadius: 99, fontSize: 10, fontWeight: 700,
              fontFamily: V.font, textTransform: 'uppercase', letterSpacing: '1px',
              background: `${captionStatus.color}20`, color: captionStatus.color,
            }}>
              {captionStatus.label}
            </span>
            <button type="button" onClick={handleRecaption}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: V.textMuted, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: V.font }}>
              <RefreshCw size={11} /> Regenerate
            </button>
          </div>
          {asset.caption?.description ? (
            <>
              <div style={{ fontSize: 13, color: V.textSub, lineHeight: 1.65, background: 'rgba(255,255,255,0.04)', border: `1px solid ${V.glassBorder}`, borderRadius: 12, padding: '12px 14px', fontFamily: V.font }}>
                {asset.caption.description}
              </div>
              {asset.caption.suggested_uses?.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {asset.caption.suggested_uses.map((u, i) => (
                    <span key={i} style={{ padding: '3px 10px', borderRadius: 99, background: 'rgba(255,255,255,0.06)', border: `1px solid ${V.glassBorder}`, color: V.textMuted, fontSize: 11, fontFamily: V.font }}>
                      {u}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: V.textMuted, fontStyle: 'italic', fontFamily: V.font }}>
              {asset.caption_status === 'processing' ? 'Generating caption…' : 'Caption will be generated automatically.'}
            </div>
          )}
        </Section>

        {/* Footer */}
        {asset.created_at && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid rgba(255,255,255,0.06)`, fontSize: 11, color: V.textMuted, flexShrink: 0, fontFamily: V.font }}>
            Added {new Date(asset.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}
      </motion.div>
    </>
  );
}

function Section({ label, children, style }) {
  return (
    <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid rgba(255,255,255,0.05)`, ...style }}>
      <div style={{ ...labelStyle, marginBottom: 10 }}>{label}</div>
      {children}
    </div>
  );
}
